/**
 * TikTok click / cookie attribution (isolated from Meta conversion-tracking).
 * Captures ttclid (query) + _ttp (TikTok pixel cookie) for Events API match quality.
 * Keys match BE TrackingParameter::PARAMETER_TIKTOK_TTCLID / PARAMETER_TIKTOK_TTP.
 */

const STORAGE_KEY = "mfs_tiktok_attribution";
const DUMMY_CLICK_IDS = ["", "undefined", "null", "none", "nan", "ttclid"];

const sanitizeClickId = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (DUMMY_CLICK_IDS.indexOf(raw.toLowerCase()) !== -1) return "";
  return raw;
};

const readCookie = (name) => {
  if (typeof document === "undefined") return "";
  try {
    const parts = String(document.cookie || "").split(";");
    for (let i = 0; i < parts.length; i += 1) {
      const piece = parts[i].trim();
      if (piece.indexOf(`${name}=`) === 0) {
        return decodeURIComponent(piece.slice(name.length + 1));
      }
    }
  } catch (e) {
    return "";
  }
  return "";
};

const readStorage = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
};

const writeStorage = (payload) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    /* storage unavailable */
  }
};

const queryParam = (query, key) => {
  if (query && query[key]) return String(query[key]);
  if (typeof window === "undefined" || !window.location) return "";
  try {
    return new URLSearchParams(window.location.search || "").get(key) || "";
  } catch (e) {
    return "";
  }
};

/**
 * Capture + persist TikTok click ids. Safe to call on every page load.
 * @param {{ query?: object }} [opts]
 * @returns {{ ttclid: string, _ttp: string }}
 */
export const captureTikTokAttribution = ({ query = {} } = {}) => {
  if (typeof window === "undefined") {
    return { ttclid: "", _ttp: "" };
  }

  const stored = readStorage();
  const ttclid =
    sanitizeClickId(queryParam(query, "ttclid"))
    || sanitizeClickId(stored.ttclid)
    || "";
  const ttp =
    sanitizeClickId(readCookie("_ttp"))
    || sanitizeClickId(stored._ttp)
    || "";

  const next = {
    ttclid,
    _ttp: ttp,
    updated_at: Date.now(),
  };
  if (ttclid || ttp) {
    writeStorage(next);
  }

  return { ttclid, _ttp: ttp };
};

/**
 * @returns {{ ttclid: string, _ttp: string }}
 */
export const getTikTokClickIds = () => {
  const live = captureTikTokAttribution();
  return {
    ttclid: live.ttclid || "",
    _ttp: live._ttp || "",
  };
};

/**
 * Flat fields to merge into register/checkout tracking bodies for BE.
 * Only includes present keys (no empty strings).
 */
export const buildTikTokTrackingPayload = ({ query = {} } = {}) => {
  const ids = captureTikTokAttribution({ query });
  const payload = {};
  if (ids.ttclid) payload.ttclid = ids.ttclid;
  if (ids._ttp) payload._ttp = ids._ttp;
  return payload;
};
